import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { TASK_STATUSES, TASK_PRIORITIES, TASK_TYPES } from '@krit/core';
import type { TaskStatus, TaskPriority, TaskType } from '@krit/core';
import {
  getDealTasks, createTask, patchTask, deleteTaskApi, getTeam, getMe, canEdit,
  type DealTask, type TaskInput, type TeamMember,
} from '../lib/api.js';
import { Card, Pill, Spinner } from './ui.js';

/**
 * Per-deal task / to-do list. A deal's tasks are grouped by status (To do / In progress / Done)
 * with an inline add + edit form. `TaskItem` and `TaskForm` are exported so the cross-deal
 * "My Tasks" page (pages/MyTasks.tsx) renders the same rows. The deal's owner/lead is the deal's
 * `poc` — surfaced elsewhere; here we track who owns each individual task (the assignee).
 */

export const STATUS_META: Record<TaskStatus, { label: string; tone: string }> = {
  todo: { label: 'To do', tone: 'gray' },
  doing: { label: 'In progress', tone: 'amber' },
  done: { label: 'Done', tone: 'green' },
};
export const PRIORITY_META: Record<TaskPriority, { label: string; tone: string }> = {
  low: { label: 'Low', tone: 'gray' },
  med: { label: 'Med', tone: 'teal' },
  high: { label: 'High', tone: 'red' },
};
export const TYPE_META: Record<TaskType, { label: string }> = {
  todo: { label: 'To-do' },
  review: { label: 'Review' },
};

const inp = 'px-2.5 py-1.5 rounded-md border border-line bg-white text-[12.5px] text-navy focus:outline-none focus:border-teal';
const btnPrimary = 'px-3 py-1.5 rounded-md text-[12px] font-semibold bg-teal text-white hover:bg-teal-d disabled:opacity-50';
const btnGhost = 'px-2.5 py-1 rounded-md text-[11.5px] font-semibold text-navy border border-line bg-white hover:bg-canvas';

const toDateInput = (iso: string | null | undefined) => (iso ? new Date(iso).toISOString().slice(0, 10) : '');
const fmtDue = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : '');
const isOverdue = (t: DealTask) => !!t.dueDate && t.status !== 'done' && new Date(t.dueDate) < new Date(new Date().toDateString());

const tagList = (s: string) => s.split(',').map((x) => x.trim()).filter(Boolean);

/** Create/edit form, shared by the add row and per-task edit. */
export function TaskForm({ team, initial, submitting, onSubmit, onCancel }: {
  team: TeamMember[];
  initial?: DealTask;
  submitting: boolean;
  onSubmit: (input: TaskInput) => void;
  onCancel: () => void;
}) {
  const [title, setTitle] = useState(initial?.title ?? '');
  const [detail, setDetail] = useState(initial?.detail ?? '');
  const [assigneeId, setAssigneeId] = useState(initial?.assignee?.id ?? '');
  const [due, setDue] = useState(toDateInput(initial?.dueDate));
  const [priority, setPriority] = useState<TaskPriority>(initial?.priority ?? 'med');
  const [type, setType] = useState<TaskType>(initial?.type ?? 'todo');
  const [tags, setTags] = useState((initial?.tags ?? []).join(', '));

  const submit = () => {
    const t = title.trim();
    if (!t) return;
    onSubmit({ title: t, detail: detail.trim() || null, assigneeId: assigneeId || null, dueDate: due || null, priority, type, tags: tagList(tags) });
  };

  return (
    <div className="rounded-lg border border-line-2 bg-canvas p-3 space-y-2.5">
      <input className={`${inp} w-full`} placeholder="Task title…" value={title} autoFocus
        onChange={(e) => setTitle(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) submit(); }} />
      <textarea className={`${inp} w-full resize-y min-h-[52px]`} placeholder="Details (optional)…" value={detail} onChange={(e) => setDetail(e.target.value)} />
      <div className="flex flex-wrap gap-2">
        <label className="flex flex-col gap-1 text-[10.5px] font-semibold uppercase tracking-wide text-muted-2">Owner
          <select className={inp} value={assigneeId} onChange={(e) => setAssigneeId(e.target.value)}>
            <option value="">Unassigned</option>
            {team.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-[10.5px] font-semibold uppercase tracking-wide text-muted-2">Due
          <input type="date" className={inp} value={due} onChange={(e) => setDue(e.target.value)} />
        </label>
        <label className="flex flex-col gap-1 text-[10.5px] font-semibold uppercase tracking-wide text-muted-2">Priority
          <select className={inp} value={priority} onChange={(e) => setPriority(e.target.value as TaskPriority)}>
            {TASK_PRIORITIES.map((p) => <option key={p} value={p}>{PRIORITY_META[p].label}</option>)}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-[10.5px] font-semibold uppercase tracking-wide text-muted-2">Type
          <select className={inp} value={type} onChange={(e) => setType(e.target.value as TaskType)}>
            {TASK_TYPES.map((t) => <option key={t} value={t}>{TYPE_META[t].label}</option>)}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-[10.5px] font-semibold uppercase tracking-wide text-muted-2 flex-1 min-w-[160px]">Tags (comma-separated)
          <input className={`${inp} w-full`} placeholder="legal, IC, diligence" value={tags} onChange={(e) => setTags(e.target.value)} />
        </label>
      </div>
      <div className="flex items-center gap-2">
        <button type="button" className={btnPrimary} disabled={submitting || !title.trim()} onClick={submit}>
          {submitting ? <span className="inline-flex items-center gap-1.5"><Spinner size={12} /> Saving…</span> : initial ? 'Save' : 'Add task'}
        </button>
        <button type="button" className={btnGhost} onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

/** One task row. `showDeal` renders a deal chip (used by the cross-deal My Tasks view). */
export function TaskItem({ task, team, editable, showDeal = false, busy, onPatch, onDelete }: {
  task: DealTask;
  team: TeamMember[];
  editable: boolean;
  showDeal?: boolean;
  busy: boolean;
  onPatch: (id: string, patch: TaskInput) => void;
  onDelete: (id: string) => void;
}) {
  const [editing, setEditing] = useState(false);
  if (editing) {
    return <TaskForm team={team} initial={task} submitting={busy} onSubmit={(p) => { onPatch(task.id, p); setEditing(false); }} onCancel={() => setEditing(false)} />;
  }
  const done = task.status === 'done';
  const overdue = isOverdue(task);
  return (
    <div className="flex items-start gap-2.5 py-2.5 border-b border-line-2 last:border-0">
      <input type="checkbox" className="mt-0.5 h-4 w-4 accent-teal cursor-pointer disabled:cursor-default" checked={done} disabled={!editable || busy}
        title={done ? 'Mark as to-do' : 'Mark as done'} onChange={() => onPatch(task.id, { status: done ? 'todo' : 'done' })} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`text-[13px] font-semibold ${done ? 'line-through text-muted-2' : 'text-navy'}`}>{task.title}</span>
          {task.type === 'review' && <Pill tone="purple">Review</Pill>}
          {!done && <Pill tone={PRIORITY_META[task.priority].tone}>{PRIORITY_META[task.priority].label}</Pill>}
        </div>
        {task.detail && <p className="text-[12px] text-muted mt-0.5 whitespace-pre-wrap">{task.detail}</p>}
        <div className="flex items-center gap-x-3 gap-y-1 flex-wrap mt-1 text-[11px] text-muted-2">
          {task.assignee
            ? <span className="inline-flex items-center gap-1"><span className="inline-flex items-center justify-center h-4 w-4 rounded-full bg-navy-50 text-navy text-[8.5px] font-bold">{task.assignee.initials}</span>{task.assignee.name}</span>
            : <span className="italic">Unassigned</span>}
          {task.dueDate && <span className={overdue ? 'text-red font-semibold' : ''}>📅 {fmtDue(task.dueDate)}{overdue ? ' · overdue' : ''}</span>}
          {showDeal && task.deal && <Link to={`/deals/${task.deal.id}`} className="text-teal-d hover:underline font-semibold">{task.deal.short}</Link>}
          {task.tags.map((t) => <span key={t} className="px-1.5 py-0.5 rounded bg-[#eef1f5] text-muted">#{t}</span>)}
        </div>
      </div>
      {editable && (
        <div className="flex items-center gap-1.5 flex-none">
          <select className={`${inp} py-1`} value={task.status} disabled={busy} title="Status"
            onChange={(e) => onPatch(task.id, { status: e.target.value as TaskStatus })}>
            {TASK_STATUSES.map((s) => <option key={s} value={s}>{STATUS_META[s].label}</option>)}
          </select>
          <button type="button" className={btnGhost} title="Edit" onClick={() => setEditing(true)}>✎</button>
          <button type="button" className="px-2 py-1 rounded-md text-[12px] text-muted-2 hover:text-red hover:bg-red-50" title="Delete"
            disabled={busy} onClick={() => { if (confirm('Delete this task?')) onDelete(task.id); }}>✕</button>
        </div>
      )}
    </div>
  );
}

/** The per-deal Tasks tab. */
export function DealTasks({ dealId }: { dealId: string }) {
  const qc = useQueryClient();
  const meQ = useQuery({ queryKey: ['me'], queryFn: getMe, staleTime: 300_000 });
  const teamQ = useQuery({ queryKey: ['team'], queryFn: getTeam });
  const tasksQ = useQuery({ queryKey: ['tasks', dealId], queryFn: () => getDealTasks(dealId) });

  const team = (teamQ.data?.team ?? []).filter((m) => m.active);
  const editable = canEdit(meQ.data?.identity);
  const [adding, setAdding] = useState(false);

  const invalidate = () => qc.invalidateQueries({ queryKey: ['tasks', dealId] });
  const createM = useMutation({ mutationFn: (b: TaskInput) => createTask(dealId, b), onSuccess: () => { invalidate(); setAdding(false); } });
  const patchM = useMutation({ mutationFn: ({ id, patch }: { id: string; patch: TaskInput }) => patchTask(id, patch), onSuccess: invalidate });
  const delM = useMutation({ mutationFn: (id: string) => deleteTaskApi(id), onSuccess: invalidate });
  const busy = createM.isPending || patchM.isPending || delM.isPending;

  const tasks = tasksQ.data?.tasks ?? [];
  const open = tasks.filter((t) => t.status !== 'done').length;

  return (
    <Card
      title="Tasks"
      sub={tasks.length ? `${open} open · ${tasks.length} total` : 'To-dos, reviews and ownership for this deal'}
      actions={editable && <button type="button" className={btnPrimary} onClick={() => setAdding((a) => !a)}>{adding ? 'Close' : '＋ Add task'}</button>}
    >
      {adding && editable && (
        <div className="mb-3">
          <TaskForm team={team} submitting={createM.isPending} onSubmit={(p) => createM.mutate(p)} onCancel={() => setAdding(false)} />
        </div>
      )}
      {(createM.error || patchM.error || delM.error) && (
        <p className="text-[12px] text-red mb-2">{String((createM.error || patchM.error || delM.error) as Error)}</p>
      )}
      {tasksQ.isLoading ? (
        <div className="flex items-center gap-2 text-muted text-[12.5px] py-4"><Spinner size={14} /> Loading tasks…</div>
      ) : tasks.length === 0 ? (
        <div className="text-center text-muted text-[12.5px] py-6">No tasks yet.{editable && ' Add the first to-do or review above.'}</div>
      ) : (
        TASK_STATUSES.map((s) => {
          const items = tasks.filter((t) => t.status === s);
          if (!items.length) return null;
          return (
            <section key={s} className="mb-3 last:mb-0">
              <h4 className="text-[10.5px] font-bold uppercase tracking-[0.5px] text-muted-2 mb-1">{STATUS_META[s].label} · {items.length}</h4>
              {items.map((t) => (
                <TaskItem key={t.id} task={t} team={team} editable={editable} busy={busy}
                  onPatch={(id, patch) => patchM.mutate({ id, patch })} onDelete={(id) => delM.mutate(id)} />
              ))}
            </section>
          );
        })
      )}
    </Card>
  );
}
